import { useState } from "react";
import { useIntl } from "react-intl";
import { IForm } from "../../";
import { lastSaveConflict } from "../../../../utils/lastSaveConflict";
import VehicleChildForm from "./components/vehicleChildForm";
import { getRequiredFields } from "./utils/required-fields-by-step";
import DealershipChildForm from "./components/dealershipChildForm";
import ServiceChildForm from "./components/serviceChildForm";
import ErrorDialog from "../../../../components/dialogs/ErrorDialog";
import SuccessDialog from "../../../../components/dialogs/SuccessDialog";
import { formatFieldLabel } from "./utils/fields-labels";
import { Occurrence } from "../../../../models/occurrence";

const ReleaseForm = (props: IForm) => {
  const intl = useIntl();
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);
  const [hasError, setHasError] = useState(false);

  const requiredServiceFields = getRequiredFields(
    props.occurrence?.occurrenceType === "P" ? "RELEASE_PREVENTIVE_CHECKUP" : "RELEASE",
    props.occurrence?.occurrenceType
  );

  const isDealership = props.occurrence?.occurrenceType === 'D';
  const serviceHiddenFields = isDealership
    ? ['estimatedTime','partOrderStatus','osNumber','osDtOpenAt','driverCheckInDriver','mechanicLocation','towTruckLocation','literatureDiagnosis','literatureTroubleshooting','tasNumber','tasStatus','checklist','criticality','createdBy','dtStart','dtEnd','modules','estimateTimeRepair','visualInspection','vehiclePayloadType','vehicleMaximumPayload','vehicleCriticalPayload']
    : ['timeOpenProtocol','country','driverCheckInDriver','mechanicLocation','towTruckLocation','literatureDiagnosis','literatureTroubleshooting','vehiclePayloadType','vehicleMaximumPayload','vehicleCriticalPayload','modules','partOrderStatus'];

  return (
    <div>
      <div className="text-size-h4 font-bold">{intl.formatMessage({ id: "intl-msg:occurrence.step.release" })}</div>
      <div className="text-color-status-resting text-size-14">
        {intl.formatMessage({ id: "intl-msg:kanban.form.releaseForm.description" })}
      </div>

      <VehicleChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={["assetId", "protocolNumber"]}
        disabledFields={["vehicleChassis"]}
        disabledAllFields={true}
        requiredFields={[]}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
        scheduling={props.scheduling}
      />

      <DealershipChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={[
          "dealershipId",
          "dealershipUniqueId",
          "dealershipLocation",
          "dealershipRepresentative",
          "dealershipRegional",
          "dealershipPhone",
          "dealershipDemarcatedArea",
        ]}
        disabledFields={[]}
        disabledAllFields={true}
        requiredFields={[]}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
      />

      <ServiceChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={serviceHiddenFields}
        disabledFields={[
          "estimateTimeRepair",
          "criticality",
          "createdBy",
          "occurrenceType",
          "source",
          "hasLink",
          "mainOccurrence",
          "tasNumber",
          "tasStatus",
          "partOrderParts",
          "partOrderNumber",
          "partOrderStatusOrder",
          "partOrderDtEstimate",
          "partOrderDtDeliveryEstimate",
          "serviceBaySchedule",
          "osNumber",
          "osDtOpenAt",
        ]}
        disabledAllFields={props.disabledAllFields}
        requiredFields={requiredServiceFields}
        step={props.step}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        partners={props.partners}
        hasError={hasError}
        persona={props.persona}
        scheduling={props.scheduling}
      />

      <hr className="my-6" />

      {props.persona !== "MANAGER" &&
        !props.finalized &&
        !props.hiddenSaveButton && (
          <div className="flex justify-end">
            <button
              className={`btn btn-primary ${
                props.loading ? "btn-loading disabled" : ""
              }`}
              type="button"
              onClick={async () => {
                if (!props.occurrence) return;
                const isValid = props.isValidStep(requiredServiceFields, props.occurrence);
                setHasError(!isValid);
                if (!isValid) {
                  setShowValidationErrorDialog(true);
                  return;
                }
                const success = await props.save(props.occurrence);
                if (success) {
                  setShowSuccessDialog(true);
                } else if (!lastSaveConflict.current) {
                  setShowErrorDialog(true);
                }
              }}
              disabled={props.loading || props.disabledAllFields}
            >
              {intl.formatMessage({ id: "intl-msg:occurrence.save" })}
            </button>
          </div>
        )}

      <ErrorDialog
        show={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title={intl.formatMessage({ id: "intl-msg:kanban.form.releaseForm.saveErrorTitle" })}
        body={intl.formatMessage({ id: "intl-msg:kanban.form.releaseForm.saveErrorBody" })}
      />

      <ErrorDialog
        show={showValidationErrorDialog}
        onClose={() => setShowValidationErrorDialog(false)}
        title={intl.formatMessage({ id: "intl-msg:kanban.form.releaseForm.missingPartTitle" })}
        body={intl.formatMessage({ id: "intl-msg:kanban.form.releaseForm.missingPartBody" })}
      />

      <SuccessDialog
        show={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title={intl.formatMessage({ id: "intl-msg:kanban.form.releaseForm.successTitle" })}
        body={
          <div className="space-y-4">
            <div>{intl.formatMessage({ id: "intl-msg:kanban.form.releaseForm.successBody" })}</div>
            {hasError && (
              <>
                <div>
                  {intl.formatMessage({ id: "intl-msg:occurrence.step.errorOccurrenceBody" })}
                </div>
                <div>{intl.formatMessage({ id: "intl-msg:occurrence.step.requiredFields" })}</div>
                <ul className="list-disc list-inside">
                  {props
                    .getNotFilledRequiredFields(
                      requiredServiceFields,
                      props.occurrence ?? ({} as Occurrence)
                    )
                    .map((field: string) => (
                      <li key={field}>
                        {formatFieldLabel(intl, field)}
                      </li>
                    ))}
                </ul>
              </>
            )}
          </div>
        }
      />
    </div>
  );
};

export default ReleaseForm;
